import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Terminal } from '@xterm/xterm';
import { FitAddon } from '@xterm/addon-fit';
import { WebLinksAddon } from '@xterm/addon-web-links';
import '@xterm/xterm/css/xterm.css';
import { api, consoleWsUrl, type ConsoleSessionMeta, type ConsoleContainer, type Me } from '../api';
import { Page, Card, Button, Field, Select, Input, Badge, EmptyState } from '../ui';

/** Terminal xterm conectado ao WebSocket de uma sessão. Admin escreve; demais espelham. */
function XtermView({ sessionId }: { sessionId: string }) {
  const hostRef = useRef<HTMLDivElement>(null);
  const [status, setStatus] = useState<'connecting' | 'open' | 'closed'>('connecting');
  const [canWrite, setCanWrite] = useState(false);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    const term = new Terminal({
      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
      fontSize: 13,
      cursorBlink: true,
      scrollback: 8000,
      theme: { background: '#0b0e14', foreground: '#cdd6f4', cursor: '#cdd6f4', selectionBackground: '#33415580' },
    });
    const fit = new FitAddon();
    term.loadAddon(fit);
    term.loadAddon(new WebLinksAddon());
    term.open(host);
    try {
      fit.fit();
    } catch {
      /* ignore */
    }

    let writable = false;
    const ws = new WebSocket(consoleWsUrl(sessionId));
    ws.binaryType = 'arraybuffer';

    ws.onopen = () => setStatus('open');
    ws.onclose = () => setStatus('closed');
    ws.onerror = () => setStatus('closed');
    ws.onmessage = (ev: MessageEvent) => {
      if (typeof ev.data === 'string') {
        try {
          const m = JSON.parse(ev.data) as { bp?: boolean; type?: string; canWrite?: boolean; reason?: string; error?: string };
          if (!m.bp) return;
          if (m.type === 'ready') {
            writable = Boolean(m.canWrite);
            setCanWrite(writable);
            term.options.disableStdin = !writable;
            // sincroniza o tamanho do PTY remoto com o terminal atual
            if (writable) ws.send(JSON.stringify({ t: 'r', c: term.cols, r: term.rows }));
          } else if (m.type === 'closed') {
            term.writeln(`\r\n\x1b[33m[${m.reason ?? 'encerrado'}]\x1b[0m`);
            setStatus('closed');
          } else if (m.type === 'error') {
            term.writeln(`\r\n\x1b[31m[erro: ${m.error}]\x1b[0m`);
          }
        } catch {
          /* ignore */
        }
      } else {
        term.write(new Uint8Array(ev.data as ArrayBuffer));
      }
    };

    const dataSub = term.onData((d) => {
      if (writable && ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ t: 'i', d }));
    });
    const resizeSub = term.onResize(({ cols, rows }) => {
      if (writable && ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ t: 'r', c: cols, r: rows }));
    });

    const onWinResize = () => {
      try {
        fit.fit();
      } catch {
        /* ignore */
      }
    };
    window.addEventListener('resize', onWinResize);
    const ro = new ResizeObserver(onWinResize);
    ro.observe(host);

    return () => {
      window.removeEventListener('resize', onWinResize);
      ro.disconnect();
      dataSub.dispose();
      resizeSub.dispose();
      try {
        ws.close();
      } catch {
        /* ignore */
      }
      term.dispose();
    };
  }, [sessionId]);

  return (
    <div className="console-term-wrap">
      <div className="console-term-bar">
        <Badge variant={status === 'open' ? 'ok' : status === 'connecting' ? 'accent' : 'neutral'}>{status}</Badge>
        <span className="users-muted">{canWrite ? 'você pode digitar' : 'somente leitura (espelho)'}</span>
      </div>
      <div ref={hostRef} className="console-term" />
    </div>
  );
}

const KIND_LABEL: Record<string, string> = { logs: 'logs', shell: 'shell' };

export function Console() {
  const { slug } = useParams<{ slug?: string }>();
  // Base do projeto p/ filtrar containers (ex.: 'mistik-island' → 'mistik').
  const projectBase = slug ? (slug.split('-')[0] ?? '').toLowerCase() : '';
  const [me, setMe] = useState<Me | null>(null);
  const [sessions, setSessions] = useState<ConsoleSessionMeta[]>([]);
  const [active, setActive] = useState<string | null>(null);
  const [containers, setContainers] = useState<ConsoleContainer[]>([]);
  const [showAll, setShowAll] = useState(false);
  const [kind, setKind] = useState<'logs' | 'shell'>('logs');
  const [target, setTarget] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const isAdmin = me?.globalRole === 'admin';

  function reload() {
    api.listConsoleSessions().then(({ sessions }) => setSessions(sessions)).catch(() => undefined);
  }

  useEffect(() => {
    api.me().then(({ user }) => setMe(user)).catch(() => undefined);
    reload();
    const t = setInterval(reload, 4000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (!isAdmin) return;
    api.listConsoleContainers().then(({ containers }) => setContainers(containers)).catch(() => undefined);
  }, [isAdmin]);

  async function open(input: { kind: 'logs' | 'shell'; target: string; title?: string }) {
    if (busy) return;
    setBusy(true);
    setErr(null);
    try {
      const { session } = await api.createConsoleSession(input);
      reload();
      setActive(session.id);
    } catch (e) {
      const c = (e as { error?: string }).error;
      setErr(c === 'too_many_sessions' ? 'Limite de consoles atingido.' : c === 'invalid_target' ? 'Alvo inválido.' : 'Falha ao abrir.');
    } finally {
      setBusy(false);
    }
  }

  async function destroy(id: string) {
    await api.closeConsoleSession(id).catch(() => undefined);
    if (active === id) setActive(null);
    reload();
  }

  const activeSession = sessions.find((s) => s.id === active);
  const projectContainers = projectBase ? containers.filter((c) => c.name.toLowerCase().includes(projectBase)) : containers;
  const visibleContainers = slug && !showAll && projectContainers.length > 0 ? projectContainers : containers;

  return (
    <Page
      title={slug ? 'Console do projeto' : 'Console'}
      subtitle={slug
        ? 'Logs ao vivo dos containers deste projeto (e shell). Espelhado em tempo real; marque "ver todos" pra outros containers.'
        : 'Terminais ao vivo: acompanhe logs e rode comandos em containers e no host. Espelhado em tempo real.'}
    >
      {err && <div className="chat-error">{err}</div>}

      {isAdmin && (
        <Card style={{ marginBottom: 16 }}>
          <div className="card-title-row">
            <strong>Abrir console</strong>
            <div className="console-quick">
              <Button size="sm" variant="secondary" onClick={() => open({ kind: 'logs', target: 'bugpilot-orchestrator', title: 'Claude Code (orchestrator)' })}>
                ✨ Claude Code
              </Button>
              <Button size="sm" variant="ghost" onClick={() => open({ kind: 'shell', target: 'host', title: 'shell · host' })}>
                shell no host
              </Button>
            </div>
          </div>
          <div className="console-create-row">
            <Field label="Tipo">
              <Select value={kind} onChange={(e) => setKind(e.target.value as 'logs' | 'shell')}>
                <option value="logs">logs (acompanhar)</option>
                <option value="shell">shell (interativo)</option>
              </Select>
            </Field>
            <Field label="Alvo">
              {containers.length > 0 ? (
                <Select value={target} onChange={(e) => setTarget(e.target.value)}>
                  <option value="">selecione…</option>
                  {kind === 'shell' && <option value="host">host (VPS)</option>}
                  {visibleContainers.map((c) => (
                    <option key={c.name} value={c.name}>
                      {c.name} — {c.image}
                    </option>
                  ))}
                </Select>
              ) : (
                <Input value={target} onChange={(e) => setTarget(e.target.value)} placeholder="nome do container ou 'host'" />
              )}
            </Field>
            {slug && projectContainers.length > 0 && projectContainers.length < containers.length && (
              <Field label=" ">
                <label className="agent-editor-toggle">
                  <input type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} />
                  <span>ver todos os containers</span>
                </label>
              </Field>
            )}
            <Button variant="primary" onClick={() => target && open({ kind, target })} disabled={busy || !target}>
              {busy ? 'abrindo…' : 'abrir'}
            </Button>
          </div>
        </Card>
      )}

      <div className="console-layout">
        <Card className="console-list">
          <div className="card-title-row"><strong>Consoles ativos</strong></div>
          {sessions.length === 0 ? (
            <EmptyState title="Nenhum console" description={isAdmin ? 'Abra um console acima.' : 'Aguarde um admin abrir um console.'} />
          ) : (
            <ul className="console-session-list">
              {sessions.map((s) => (
                <li key={s.id} className={`console-session-item ${active === s.id ? 'is-active' : ''}`}>
                  <button className="console-session-btn" onClick={() => setActive(s.id)}>
                    <Badge variant={s.closed ? 'neutral' : s.kind === 'shell' ? 'accent' : 'ok'}>{KIND_LABEL[s.kind]}</Badge>
                    <span className="console-session-title">{s.title}</span>
                    <span className="users-muted">{s.viewers}👁 · {s.ownerName}</span>
                  </button>
                  {isAdmin && (
                    <button className="console-session-x" title="encerrar" onClick={() => destroy(s.id)}>✕</button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card className="console-main">
          {activeSession ? (
            <>
              <div className="card-title-row">
                <strong>{activeSession.title}</strong>
                <span className="users-muted">{activeSession.interactive ? 'interativo' : 'somente leitura'} · {activeSession.target}</span>
              </div>
              <XtermView key={activeSession.id} sessionId={activeSession.id} />
            </>
          ) : (
            <EmptyState title="Selecione um console" description="Escolha um console à esquerda para ver o stream ao vivo." />
          )}
        </Card>
      </div>
    </Page>
  );
}
